<template>
    <!-- PRODUCT-26110 Task 12b (INSTANCE_ATTRS_CLASS_STYLE): Vue 3 folds class/style
         into $attrs and stops auto-applying them to root when inheritAttrs is false,
         so a caller's `class=` (e.g. PolicyInfo.vue's "PolicyInfo__jsonSchemaView")
         would silently stop reaching this component's real root. Bind them
         explicitly to keep the old Vue 2 fallthrough behavior. -->
    <JsonSchemaViewElement
        :id="id"
        :value="value"
        :form="form"
        :currency="currency"
        :custom-component-input="components"
        :evaluation-errors="evaluationErrors"
        :start-index="startIndex"
        :no-object-index="noObjectIndex"
        :class="$attrs.class"
        :style="$attrs.style"
        v-bind="listenerAttrs"
    />
</template>

<script lang="ts">
    import { markRaw, type Component as VueComponent } from 'vue';

    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormBase, JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaObject, JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { pickListenerAttrs } from '@evidentid/vue-commons/utils/attrsListeners';

    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import JsonSchemaArrayView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaArrayView.vue';
    import JsonSchemaBooleanView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaBooleanView.vue';
    import JsonSchemaNumberView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaNumberView.vue';
    import JsonSchemaObjectView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaObjectView.vue';
    import JsonSchemaStringView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaStringView.vue';
    import JsonSchemaTextView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaTextView.vue';
    import JsonSchemaViewElement from '@/modules/entity-details/components/JsonSchemaView/JsonSchemaViewElement.vue';

    // TODO(PRODUCT-17637): merging CustomJsonForms dir with JsonSchemaView's elements, as they may have overlaps
    @Component({
        inheritAttrs: false,
        components: { JsonSchemaViewElement },
    })
    export default class JsonSchemaView extends Vue {
        @Prop(Object)
        private schema!: JsonSchemaObject;

        @Prop()
        private value!: any;

        @Prop({ type: Object, default: () => null })
        private customComponents!: Partial<Record<JsonFormType, VueComponent>> | null;

        @Prop(String)
        private id!: string;

        @Prop({ type: Object, default: () => ({}) })
        private evaluationErrors!: Record<string, FieldEvaluationResultError>;

        @Prop({ type: String, default: 'USD' })
        private currency!: string;

        @Prop({ type: Number, default: 1 })
        private startIndex !: number;

        @Prop({ type: Boolean, default: false })
        private noObjectIndex !: boolean;

        // markRaw: a class property lands in reactive data, so the `components` getter
        // below would spread proxied components into `custom-component-input` and Vue
        // warns when one reaches <component :is>. This map is a constant, never state.
        private defaultComponents: Record<JsonSchemaType, VueComponent> = markRaw({
            array: JsonSchemaArrayView,
            string: JsonSchemaStringView,
            number: JsonSchemaNumberView,
            integer: JsonSchemaTextView,
            boolean: JsonSchemaBooleanView,
            object: JsonSchemaObjectView,
        });

        private form: JsonFormBase<JsonSchemaObject> | null = null;

        private get components() {
            return { ...this.defaultComponents, ...this.customComponents };
        }

        @Watch('schema', { immediate: true })
        private buildForm(): void {
            this.form = createForm(this.schema);
        }

        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): this component is `inheritAttrs:
        // false` and never spreads raw `$attrs` -- only declared props + listeners are
        // forwarded to JsonSchemaViewElement. Vue 3 merges listeners into `$attrs`, so
        // pick just the `onXxx` keys back out instead of a blanket `v-bind="$attrs"`.
        private get listenerAttrs(): Record<string, unknown> {
            return pickListenerAttrs(this.$attrs);
        }
    }
</script>
